import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Select from "react-select";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import Modal from "./Modal";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { graficaApi } from "../../api/graficaApi";

/* =======================
   ESTILOS
======================= */

const Container = styled.div`
  font-size: var(--app-font-size);
`;

const ChartContainer = styled.div`
  margin-bottom: 10px;
`;

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  background-color: #f0f0f0;
  padding: 10px;
  border-radius: 5px;
  cursor: pointer;
  position: relative;
`;

const TitleCentered = styled.h2`
  flex: 1;
  text-align: center;
  color: #004aad;
  font-size: var(--app-font-size);
  margin: 0;
`;

const IconWrapper = styled.div`
  position: absolute;
  right: 15px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const CustomSelect = styled.div`
  width: 220px;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 10px;
`;

const TableCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 6px;
  background-color: #fff;
`;

/* =======================
   TABLA (TIPO N)
======================= */

const TablaResultados = ({ title, data, expanded, onToggle }) => {
  return (
    <TableCard>
      <AccordionHeader onClick={onToggle}>
        <TitleCentered>{title}</TitleCentered>
        <IconWrapper>
          {expanded ? <FaChevronUp /> : <FaChevronDown />}
        </IconWrapper>
      </AccordionHeader>

      {expanded && (
        <div style={{ padding: 10, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f0f0f0" }}>
                <th>Fecha</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={i}>
                  <td>{row.date}</td>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </TableCard>
  );
};

/* =======================
   MODAL PRINCIPAL
======================= */

const GraficaModal = ({ isOpen, onClose, ident, tipoId }) => {
  const [charts, setCharts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [filter, setFilter] = useState(null);
  const [monthRange, setMonthRange] = useState({
    value: 10,
    label: "Último año",
  });
  const [loading, setLoading] = useState(true);

  const monthOptions = [
    { value: 10, label: "Último año" },
    { value: 30, label: "Últimos 3 años" },
    { value: 50, label: "Últimos 5 años" },
  ];

  useEffect(() => {
    if (!isOpen || !ident || !tipoId) return;

    const loadData = async () => {
      try {
        setLoading(true);

        const result = await graficaApi.getGraficas(
          ident,
          tipoId,
          monthRange.value
        );

        const data = result.GetDatosGrafica || [];
        setCharts(data);

        const init = {};
        data.forEach((c, i) => {
          if (i < 3) init[c.title] = true;
        });
        setExpanded(init);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, ident, tipoId, monthRange]);

  const options = charts.map((c) => ({
    value: c.title,
    label: c.title,
  }));

  const filteredCharts = filter
    ? charts.filter((c) => c.title === filter.value)
    : charts;

  const graficas = filteredCharts.filter((c) => c.tipo === "D");
  const tablas = filteredCharts.filter((c) => c.tipo === "N");

  const toggleChart = (title) => {
    setExpanded((p) => ({ ...p, [title]: !p[title] }));
  };

  const toggleRow = (rowIndex) => {
    setExpandedRows((p) => ({
      ...p,
      [rowIndex]: !p[rowIndex],
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="90vw"
      title="Histórico Paciente"
    >
      <Container>
        {loading ? (
          <p>Cargando...</p>
        ) : (
          <>
            <FilterRow>
              <CustomSelect>
                <Select
                  options={options}
                  value={filter}
                  onChange={setFilter}
                  isClearable
                  placeholder="Buscar..."
                />
              </CustomSelect>

              <CustomSelect>
                <Select
                  options={monthOptions}
                  value={monthRange}
                  onChange={setMonthRange}
                />
              </CustomSelect>
            </FilterRow>

            {/* ===== GRÁFICAS ===== */}
            {graficas.map((chart, i) => (
              <ChartContainer key={i}>
                <AccordionHeader onClick={() => toggleChart(chart.title)}>
                  <TitleCentered>{chart.title}</TitleCentered>
                  <IconWrapper>
                    {expanded[chart.title] ? (
                      <FaChevronUp />
                    ) : (
                      <FaChevronDown />
                    )}
                  </IconWrapper>
                </AccordionHeader>

                {expanded[chart.title] && (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chart.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" angle={-45} textAnchor="end" />
                      <YAxis />
                      <Tooltip />
                      <ReferenceLine y={chart.min} stroke="red" />
                      <ReferenceLine y={chart.max} stroke="green" />
                      <Line dataKey="value" stroke="#007bff" />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </ChartContainer>
            ))}

            {/* ===== TABLAS (2 COLUMNAS, ACORDEÓN POR FILA) ===== */}
            {tablas.length > 0 && (
              <TableGrid>
                {Array.from({ length: Math.ceil(tablas.length / 2) }).map(
                  (_, rowIndex) => {
                    const left = tablas[rowIndex * 2];
                    const right = tablas[rowIndex * 2 + 1];
                    const isOpen = expandedRows[rowIndex];

                    return (
                      <React.Fragment key={rowIndex}>
                        {left && (
                          <TablaResultados
                            title={left.title}
                            data={left.data}
                            expanded={isOpen}
                            onToggle={() => toggleRow(rowIndex)}
                          />
                        )}
                        {right && (
                          <TablaResultados
                            title={right.title}
                            data={right.data}
                            expanded={isOpen}
                            onToggle={() => toggleRow(rowIndex)}
                          />
                        )}
                      </React.Fragment>
                    );
                  }
                )}
              </TableGrid>
            )}
          </>
        )}
      </Container>
    </Modal>
  );
};

export default GraficaModal;
